export enum HistoryGroupBy {
    FIELD = "field",
    SOURCE = "source",
    NONE = "none",
    DIFF_VALUE = "diffValue",
    SOURCE_COUNT_DISTINCT = "sourceCountDistinct",
}

export enum HistoryField {
    LABEL = "label",
    CATEGORY_ID = "categoryId",
    ENTITY_ID = "entityId",
    ASSIGNEE_ID = "assigneeId",
    COUNTERPARTY_ID = "counterpartyId",
    FINANCIAL_ACCOUNT_ID = "financialAccountId",
    STATUS = "status",
    AMOUNT = "amount",
    DATE = "date",
    DATE_ONLY = "dateOnly",
    DESCRIPTION = "description",
    BANK_DESCRIPTION = "bankDescription",
    NOTE = "memo",
    RECONCILED_IN_ID = "reconciledInId",
    FINANCIAL_DOCUMENT_ID = "financialDocumentId",
    PAIRED_TRANSFER_ID = "pairedTransferId",
    SPLIT_PARENT_ID = "splitParentId",
    TRANSACTION_ID = "transactionId",
    ACCOUNT_ID = "accountId",
    ACCRUAL_ACCOUNT_ID = "accrualAccountId",
    GENERAL_LEDGER_ACCOUNT_ID = "generalLedgerAccountId",
    CLASS_ASSIGNMENTS = "classIds",
    ACCOUNTS_RECEIVABLE_ID = "accountsReceivableId",
    ACCOUNTS_PAYABLE_ID = "accountsPayableId",
    LINES = "lines",
    CREATOR_ID = "creatorUserId",
    ACCOUNTING_LEDGER_ID = "accountingLedgerId",
    REVERSED_AT = "reversedAt",
    REVERSED_MANUAL_JOURNAL_ENTRY_IDS = "reversedManualJournalEntryIds",
    REVERSED_MANUAL_JOURNAL_ENTRY_ID = "reversedManualJournalEntryId",
    RECURRING_RULE_ID = "recurringRuleId",
    FINANCIAL_DOCUMENT = "financialDocument",
    BLOCKED_BY_TASKS = "blockedByTasks",
    BLOCKING_TASKS = "blockingTasks",
    RELATED_TASKS = "relatedTasks",
    TITLE = "title",
    DUE_DATE = "dueDate",
    PRIORITY = "priority",
    ESTIMATED_TIME = "estimatedTime",
    ENTITIES = "entities",
    FINANCIAL_DOCUMENTS = "financialDocuments",
    NAME = "name",
    TYPE = "type",
    EMAIL = "email",
    PHONE_NUMBER = "phoneNumber",
    WEBSITE = "website",
    STREET = "street",
    STREET2 = "street2",
    CITY = "city",
    POSTAL_CODE = "postalCode",
    STATE = "state",
    COUNTRY = "country",
    DIRECTION_OVERRIDE = "directionOverride",
    WORKSPACE_ID = "workspaceId",
    UPDATED_BY_USER_ID = "updatedByUserId",
    CREATED_FROM_GLOBAL_COUNTERPARTY_ID = "createdFromGlobalCounterpartyId",
    SHOULD_BE_NORMALIZED = "shouldBeNormalized",
    WAS_CHECKED_AGAINST_COUNTERPARTY_IDS = "wasCheckedAgainstCounterpartyIds",
    COUNTERPARTY_ENGINE_VERSION = "counterpartyEngineVersion",
    TIN_TYPE = "tinType",
    CONDITIONS = "conditions",
    ACTIONS = "actions",
    IS_DISABLED = "isDisabled",
    PARENT_CLASS_ID = "parentClassId",
    ROOT_CLASS_ID = "rootClassId",
    ORDER = "order",
    GROUP_ID = "groupId",
    RULE_NOTE = "note",
    SOURCE = "source",
    DUE = "due",
    PAID_AT = "paidAt",
    UNCOLLECTABLE_AT = "uncollectableAt",
    VOIDED_AT = "voidedAt",
    TOTAL = "total",
    SUBTOTAL = "subtotal",
    TAX = "tax",
    DISCOUNT = "discount",
    CURRENCY = "currency",
    INVOICE_NUMBER = "invoiceNumber",
    IS_SUBSCRIPTION = "isSubscription",
    ACCRUAL_MATCHES = "accrualMatches",
    FILE_NAME = "fileName",
    DOCUMENT_CATEGORY_ID = "documentCategoryId",
    LOAN_ID = "loanId",
    CLOSE_STAGE_ID = "closeStageId",
    CLOSE_ID = "closeId",
    REVIEWED_FIELD = "reviewedField",
    EXCLUDE_FROM_1099 = "excludeFrom1099",
    EXCLUDE_FROM_CASH_FLOW_REPORT = "excludeFromCashFlowReport",
    CODE = "code",
    PARENT_GROUP_ID = "parentGroupId",
    PARENT_GROUP = "parentGroup",
    ARCHIVED = "archived",
    ROLE = "role",
    RELATED_ENTITY_ID = "relatedEntityId",
    FORM_1099_BOX = "form1099Box",
    OPENING_BALANCE = "openingBalance",
    SKU = "sku",
    ACTIVE = "active",
    REVENUE_ACCOUNT_ID = "revenueAccountId",
    RECEIVABLES_ACCOUNT_ID = "receivablesAccountId",
    CATALOG_ITEM_CLASS_ASSIGNMENTS = "classAssignments",
    PRICES = "prices",
    SOURCE_TEMPLATE_ID = "sourceTemplateId",
    IS_ARCHIVED = "isArchived",
    SCHEDULE_GROUP = "group",
    TERM = "term",
    HOLDING_ACCOUNT = "holdingAccount",
    RECOGNITION_ACCOUNT = "recognitionAccount",
    POLICY = "policy",
    START_DATE = "startDate",
    RECOGNITION = "recognition",
    USER_ID = "userId",
    ASSIGN_TO_NEW_CLIENTS = "assignToNewClients",
    CLIENT_WORKSPACE_IDS = "clientWorkspaceIds",
    WORK_START_DATE = "workStartDate",
    WORK_DELIVERY_DATE = "workDeliveryDate",
    PREPARER_ID = "preparerId",
    REVIEWER_ID = "reviewerId",
    MANAGER_ID = "managerId",
}

export const HISTORY_FIELD_LABELS: Record<HistoryField, string> = {
    [HistoryField.LABEL]: "label",
    [HistoryField.CATEGORY_ID]: "category",
    [HistoryField.ENTITY_ID]: "entity",
    [HistoryField.ASSIGNEE_ID]: "assignee",
    [HistoryField.COUNTERPARTY_ID]: "counterparty",
    [HistoryField.FINANCIAL_ACCOUNT_ID]: "account",
    [HistoryField.STATUS]: "status",
    [HistoryField.AMOUNT]: "amount",
    [HistoryField.DATE]: "date",
    [HistoryField.DATE_ONLY]: "date",
    [HistoryField.DESCRIPTION]: "description",
    [HistoryField.BANK_DESCRIPTION]: "bank description",
    [HistoryField.NOTE]: "memo",
    [HistoryField.RECONCILED_IN_ID]: "reconciliation",
    [HistoryField.FINANCIAL_DOCUMENT_ID]: "document",
    [HistoryField.PAIRED_TRANSFER_ID]: "paired transaction",
    [HistoryField.SPLIT_PARENT_ID]: "split",
    [HistoryField.TRANSACTION_ID]: "transaction",
    [HistoryField.ACCOUNT_ID]: "cash ledger",
    [HistoryField.ACCRUAL_ACCOUNT_ID]: "accrual ledger",
    [HistoryField.GENERAL_LEDGER_ACCOUNT_ID]: "cash ledger",
    [HistoryField.CLASS_ASSIGNMENTS]: "class",
    [HistoryField.ACCOUNTS_RECEIVABLE_ID]: "receivables account",
    [HistoryField.ACCOUNTS_PAYABLE_ID]: "payables account",
    [HistoryField.LINES]: "line",
    [HistoryField.CREATOR_ID]: "creator",
    [HistoryField.ACCOUNTING_LEDGER_ID]: "accounting ledger",
    [HistoryField.REVERSED_AT]: "reversed at",
    [HistoryField.REVERSED_MANUAL_JOURNAL_ENTRY_IDS]:
        "reversed manual journal entry ids",
    [HistoryField.REVERSED_MANUAL_JOURNAL_ENTRY_ID]:
        "reversed manual journal entry id",
    [HistoryField.RECURRING_RULE_ID]: "recurring rule",
    [HistoryField.FINANCIAL_DOCUMENT]: "attachments",
    [HistoryField.BLOCKED_BY_TASKS]: "blocked by tasks",
    [HistoryField.BLOCKING_TASKS]: "blocking tasks",
    [HistoryField.RELATED_TASKS]: "related tasks",
    [HistoryField.TITLE]: "title",
    [HistoryField.DUE_DATE]: "due date",
    [HistoryField.PRIORITY]: "priority",
    [HistoryField.ESTIMATED_TIME]: "estimate",
    [HistoryField.ENTITIES]: "entities",
    [HistoryField.FINANCIAL_DOCUMENTS]: "documents",
    [HistoryField.NAME]: "name",
    [HistoryField.TYPE]: "type",
    [HistoryField.EMAIL]: "email",
    [HistoryField.PHONE_NUMBER]: "phone",
    [HistoryField.WEBSITE]: "website",
    [HistoryField.STREET]: "street",
    [HistoryField.STREET2]: "street 2",
    [HistoryField.CITY]: "city",
    [HistoryField.POSTAL_CODE]: "postal code",
    [HistoryField.STATE]: "state",
    [HistoryField.COUNTRY]: "country",
    [HistoryField.DIRECTION_OVERRIDE]: "classification",
    [HistoryField.WORKSPACE_ID]: "workspace",
    [HistoryField.UPDATED_BY_USER_ID]: "author",
    [HistoryField.CREATED_FROM_GLOBAL_COUNTERPARTY_ID]: "global vendor",
    [HistoryField.SHOULD_BE_NORMALIZED]: "normalization",
    [HistoryField.WAS_CHECKED_AGAINST_COUNTERPARTY_IDS]:
        "similar vendors checked",
    [HistoryField.TIN_TYPE]: "tax ID type",
    [HistoryField.COUNTERPARTY_ENGINE_VERSION]: "engine version",
    [HistoryField.CONDITIONS]: "conditions",
    [HistoryField.ACTIONS]: "actions",
    [HistoryField.IS_DISABLED]: "status",
    [HistoryField.PARENT_CLASS_ID]: "parent class",
    [HistoryField.ROOT_CLASS_ID]: "root class",
    [HistoryField.ORDER]: "order",
    [HistoryField.GROUP_ID]: "group",
    [HistoryField.RULE_NOTE]: "note",
    [HistoryField.SOURCE]: "source",
    [HistoryField.DUE]: "due date",
    [HistoryField.PAID_AT]: "paid date",
    [HistoryField.UNCOLLECTABLE_AT]: "uncollectible date",
    [HistoryField.VOIDED_AT]: "void date",
    [HistoryField.TOTAL]: "total",
    [HistoryField.SUBTOTAL]: "subtotal",
    [HistoryField.TAX]: "tax",
    [HistoryField.DISCOUNT]: "discount",
    [HistoryField.CURRENCY]: "currency",
    [HistoryField.INVOICE_NUMBER]: "number",
    [HistoryField.IS_SUBSCRIPTION]: "subscription",
    [HistoryField.ACCRUAL_MATCHES]: "accrual match",
    [HistoryField.FILE_NAME]: "file name",
    [HistoryField.DOCUMENT_CATEGORY_ID]: "category",
    [HistoryField.LOAN_ID]: "loan",
    [HistoryField.CLOSE_STAGE_ID]: "close stage",
    [HistoryField.CLOSE_ID]: "close",
    [HistoryField.REVIEWED_FIELD]: "reviewed field",
    [HistoryField.EXCLUDE_FROM_1099]: "excluded from 1099",
    [HistoryField.EXCLUDE_FROM_CASH_FLOW_REPORT]:
        "excluded from cash flow report",
    [HistoryField.CODE]: "code",
    [HistoryField.PARENT_GROUP_ID]: "group",
    [HistoryField.PARENT_GROUP]: "group path",
    [HistoryField.ARCHIVED]: "archived",
    [HistoryField.ROLE]: "role",
    [HistoryField.RELATED_ENTITY_ID]: "related business",
    [HistoryField.FORM_1099_BOX]: "1099 box",
    [HistoryField.OPENING_BALANCE]: "opening balance",
    [HistoryField.SKU]: "SKU",
    [HistoryField.ACTIVE]: "status",
    [HistoryField.REVENUE_ACCOUNT_ID]: "revenue account",
    [HistoryField.RECEIVABLES_ACCOUNT_ID]: "receivables account",
    [HistoryField.CATALOG_ITEM_CLASS_ASSIGNMENTS]: "class",
    [HistoryField.PRICES]: "price",
    [HistoryField.SOURCE_TEMPLATE_ID]: "template",
    [HistoryField.IS_ARCHIVED]: "archived",
    [HistoryField.SCHEDULE_GROUP]: "group",
    [HistoryField.TERM]: "term",
    [HistoryField.HOLDING_ACCOUNT]: "holding account",
    [HistoryField.RECOGNITION_ACCOUNT]: "recognition account",
    [HistoryField.POLICY]: "policy",
    [HistoryField.START_DATE]: "start date",
    [HistoryField.RECOGNITION]: "recognition",
    [HistoryField.USER_ID]: "member",
    [HistoryField.ASSIGN_TO_NEW_CLIENTS]: "default client access",
    [HistoryField.CLIENT_WORKSPACE_IDS]: "client access",
    [HistoryField.WORK_START_DATE]: "work start",
    [HistoryField.WORK_DELIVERY_DATE]: "work end",
    [HistoryField.PREPARER_ID]: "preparer",
    [HistoryField.REVIEWER_ID]: "reviewer",
    [HistoryField.MANAGER_ID]: "manager",
};
